import BaseService from './base.service';

/**
 * Auth Service
 * Business logic layer for authentication
 */
class AuthService extends BaseService {
  constructor() {
    super('auth');
  }

  /**
   * Login with email/phone and password
   * @param {Object} credentials - { username, password }
   * @returns {Promise|null}
   */
  async login(credentials) {
    try {
      const result = await this.dao.login(credentials);

      // Save token to localStorage (user data will be fetched by userStore)
      if (result?.payload?.token) {
        localStorage.setItem('token', result.payload.token);
      }

      return result;
    } catch {
      return null;
    }
  }

  /**
   * Login with Facebook access token
   * @param {string} accessToken - Facebook access token
   * @returns {Promise|null}
   */
  async loginWithFacebook(accessToken) {
    try {
      const result = await this.dao.loginWithFacebook({ accessToken });

      // Save token to localStorage (user data will be fetched by userStore)
      if (result?.payload?.token) {
        localStorage.setItem('token', result.payload.token);
      }

      return result;
    } catch {
      return null;
    }
  }

  /**
   * Login with Google authorization code
   * @param {string} code - Google authorization code
   * @param {string} redirectUri - Redirect URI used when getting the code
   * @returns {Promise|null}
   */
  async loginWithGoogle(code, redirectUri) {
    try {
      const result = await this.dao.loginWithGoogle({ code, redirectUri });

      // Save token to localStorage (user data will be fetched by userStore)
      if (result?.payload?.token) {
        localStorage.setItem('token', result.payload.token);
      }

      return result;
    } catch {
      return null;
    }
  }

  /**
   * Get Zalo login URL
   * @returns {Promise|null}
   */
  async getZaloLoginUrl() {
    try {
      return await this.dao.getZaloLoginUrl();
    } catch {
      return null;
    }
  }

  /**
   * Register new account
   * @param {Object} data - { email?, phone?, password, full_name }
   * @returns {Promise|null}
   */
  async register(data) {
    try {
      const result = await this.dao.register(data);

      // Auto login after register (user data will be fetched by userStore)
      if (result?.payload?.token) {
        localStorage.setItem('token', result.payload.token);
      }

      return result;
    } catch {
      return null;
    }
  }

  /**
   * Get current user info
   * @returns {Promise|null}
   */
  async getCurrentUser() {
    try {
      return await this.dao.getCurrentUser();
    } catch {
      return null;
    }
  }

  /**
   * Get current user from API (alias for backward compatibility)
   * @returns {Promise|null}
   */
  async getCurrentUserFromAPI() {
    return this.getCurrentUser();
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is logged in
   * @returns {boolean}
   */
  isLoggedIn() {
    return !!localStorage.getItem('token');
  }

  /**
   * Get token from localStorage
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Get user from localStorage
   * @returns {Object|null}
   */
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
}

export default new AuthService();

